import React, { useEffect } from 'react'
import styles from './Modal.module.scss'

export interface ModalProps {
    isOpen: boolean
    onClose: () => void
    title: string
    children: React.ReactNode
    size?: 'small' | 'medium' | 'large' | 'xlarge'
    headerActions?: React.ReactNode
    footer?: React.ReactNode
    closeOnOverlayClick?: boolean
    disableBodyScroll?: boolean
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'medium',
    headerActions,
    footer,
    closeOnOverlayClick = true,
    disableBodyScroll = false,
}) => {
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
            document.body.style.overflow = 'unset'
        }
    }, [isOpen, onClose])

    if (!isOpen) {
        return null
    }

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
            onClose()
        }
    }

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div
                className={`${styles.modalContent} ${styles[size]} ${disableBodyScroll ? styles.noMaxHeight : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2>{title}</h2>
                    {headerActions && (
                        <div className={styles.headerActions}>{headerActions}</div>
                    )}
                </div>

                <div className={`${styles.modalBody} ${disableBodyScroll ? styles.noBodyScroll : ''}`}>
                    {children}
                </div>

                {footer && (
                    <div className={styles.modalFooter}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    )
}

